/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import React, { Children, Suspense, useCallback, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import type { SizeGuide } from '../product-view/size-chart/size-guide-types';

const DIRECTIONS = {
    FORWARD: 1,
    BACKWARD: -1,
} as const;

interface SwatchChild {
    props?: {
        value?: string;
        handleSelect?: (value: string) => void;
        selected?: boolean;
        isFocusable?: boolean;
        shape?: 'color' | 'label';
        disabled?: boolean;
    };
}

// Lazy-loaded modal chunk
const SizeChartModal = React.lazy(() => import('../product-view/size-chart/size-chart-modal'));

/**
 * Props for the SwatchGroup component
 */
interface SwatchGroupProps {
    /** Accessible label for screen readers */
    ariaLabel?: string;
    /** Display name shown next to the label */
    displayName?: string;
    /** Swatch components to render within the group */
    children: React.ReactNode;
    /** Label text displayed above the swatches */
    label?: string;
    /** Currently selected swatch value */
    value?: string;
    /** Callback function called when a swatch is selected. */
    handleChange?: (value: string) => void;
    /** Additional CSS classes to apply to the container */
    className?: string;
    /** Whether it is quick add modal */
    isQuickAdd?: boolean;
    //DUMMY DATA CODE Start
    /** Parsed SizeGuide to pass directly into SizeChartModal (bypasses context lookup) */
    sizeGuide?: SizeGuide;
    //DUMMY DAT CODE END
}

const noop = (..._args: unknown[]): void => {
    // Intentionally empty - default no-op function for handleChange
};

/**
 * A container component that manages a group of swatch components with keyboard navigation and selection.
 *
 * Features:
 * - Keyboard navigation with arrow keys (wraps around at start/end)
 * - Accessible radio group implementation
 * - Automatic focus management
 * - Customizable labels and styling
 *
 * @example
 * ```tsx
 * <SwatchGroup
 *   label="Color"
 *   value={selectedColor}
 *   handleChange={setSelectedColor}
 * >
 *   <Swatch value="red" mode="hover">Red</Swatch>
 *   <Swatch value="blue" mode="hover">Blue</Swatch>
 * </SwatchGroup>
 * ```
 */
export const SwatchGroup: React.FC<SwatchGroupProps> = ({
    ariaLabel,
    displayName,
    children,
    label = '',
    value,
    handleChange = noop,
    className,
    isQuickAdd,
    //DUMMY DATA CODE Start
    sizeGuide,
    //DUMMY DAT CODE END
}) => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const [isSizeChartOpen, setIsSizeChartOpen] = useState(false);

    const onKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            const { key } = e;
            const childrenArray = Children.toArray(children);

            const move = (direction: typeof DIRECTIONS.FORWARD | typeof DIRECTIONS.BACKWARD = DIRECTIONS.FORWARD) => {
                // Find the currently focused element in the DOM to get accurate starting position
                let currentIndex = 0;
                if (typeof document !== 'undefined' && wrapperRef.current) {
                    const focusedElement = document.activeElement;
                    const elementIndex = Array.from(wrapperRef.current.children).findIndex(
                        (child) => child === focusedElement
                    );
                    if (elementIndex !== -1) {
                        currentIndex = elementIndex;
                    }
                }

                let index = currentIndex + direction;

                // Handle wrapping
                if (index >= childrenArray.length) {
                    index = 0; // Wrap to beginning
                } else if (index < 0) {
                    index = childrenArray.length - 1; // Wrap to end
                }

                const swatchEl = wrapperRef?.current?.children[index] as HTMLElement;

                // Call handleChange when navigating with keyboard
                const newChildElement = childrenArray[index] as React.ReactElement<SwatchChild['props']>;
                const newValue = newChildElement?.props?.value;
                if (newValue) {
                    handleChange(newValue);
                }

                swatchEl?.focus();
            };

            switch (key) {
                case 'ArrowUp':
                case 'ArrowLeft':
                    e.preventDefault();
                    move(DIRECTIONS.BACKWARD);
                    break;
                case 'ArrowDown':
                case 'ArrowRight':
                    e.preventDefault();
                    move(DIRECTIONS.FORWARD);
                    break;
                default:
                    break;
            }
        },
        // do not need handleChange as dep
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [children]
    );

    const containerClasses = cn('m-0 flex flex-col gap-2 min-h-[28px]', className);

    const labelClasses = cn(
        'flex items-center gap-1.5 tracking-normal text-black pdp-swatch-label',
        isQuickAdd
            ? 'font-medium text-[15px] leading-5'
            : 'font-normal text-[18px] leading-5 max-md:font-bold max-md:text-[14px]'
    );

    // Check if this is a square swatch group (size, material, etc.)
    const isSquareSwatchGroup =
        (React.Children.toArray(children)[0] as React.ReactElement<SwatchChild['props']>)?.props?.shape === 'label';

    const swatchesWrapperClasses = isSquareSwatchGroup
        ? cn(
              'inline-flex flex-wrap gap-2 focus:outline-none rounded-none',
              isQuickAdd ? 'bg-transparent p-0' : 'bg-swatch-group-bg'
          )
        : cn('flex flex-wrap gap-[var(--swatch-pill-gap,0.5rem)] focus:outline-none', isQuickAdd && 'gap-2.5');

    return (
        <div className={containerClasses} onKeyDown={onKeyDown}>
            <div
                className={cn(
                    'modal-size-label',
                    isSquareSwatchGroup ? 'inline-flex flex-col w-full' : 'flex flex-col gap-3 w-full',
                    isQuickAdd && isSquareSwatchGroup && 'pb-4 mb-4 border-b border-[#E5E7EB]',
                    isQuickAdd && !isSquareSwatchGroup && '!border-0 !pb-0 !mb-0'
                )}
                role="radiogroup"
                aria-label={ariaLabel || label}>
                {label && (
                    <div className="flex items-center justify-between w-full">
                        <div className={labelClasses}>
                            <span>{label}{isQuickAdd ? ':' : (label === 'Select Size' ? '' : ' :')}</span>
                            {displayName && <span className={cn(isQuickAdd ? 'font-bold max-md:text-[14px]' : '')}>{displayName}</span>}
                        </div>
                        {/* DUMMY DATA CODE Start */}
                        {/* {label === 'Select Size' && (
                            <button type="button" className="pdp-size-chart-btn" onClick={() => setIsSizeChartOpen(true)}>
                                Size Chart
                            </button>
                        )}
                        {label === 'Select Size' && isSizeChartOpen && (
                            <Suspense fallback={null}>
                                <SizeChartModal
                                    open={isSizeChartOpen}
                                    onOpenChange={setIsSizeChartOpen}
                                    currentSize={value}
                                    onSelectSize={(size) => handleChange(size)}
                                />
                            </Suspense>
                        )} */}
                        {Boolean(label && label.toLowerCase().includes('size')) && (
                            <button type="button" className="pdp-size-chart-btn cursor-pointer" onClick={() => setIsSizeChartOpen(true)}>
                                Size Chart
                            </button>
                        )}
                        {/*DUMMY DATA CODE Start*/}
                        {Boolean(label && label.toLowerCase().includes('size')) && isSizeChartOpen && (
                            <Suspense fallback={null}>
                                <SizeChartModal
                                    open={isSizeChartOpen}
                                    onOpenChange={setIsSizeChartOpen}
                                    currentSize={value}
                                    sizeGuide={sizeGuide}
                                    onSelectSize={(size) => handleChange(size)}
                                />
                            </Suspense>
                        )}
                        {/*DUMMY DAT CODE END*/}
                    </div>
                )}
                <div ref={wrapperRef} className={swatchesWrapperClasses}>
                    {Children.toArray(children).map((child, index) => {
                        const childElement = child as React.ReactElement<SwatchChild['props']>;
                        const selected = childElement.props?.value === value;

                        return React.cloneElement(childElement, {
                            key: childElement.props?.value || index,
                            handleSelect: handleChange,
                            selected,
                            isFocusable: value ? selected : index === 0,
                        });
                    })}
                </div>
            </div>
        </div>
    );
};

SwatchGroup.displayName = 'SwatchGroup';
